import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../store/store';
import { useAppDispatch } from '../hooks/redux';
import {
  fetchAffiliateMe,
  fetchAffiliateReferrals,
  fetchAffiliateStats,
  AffiliateReferralStatus,
} from '../store/slices/affiliateSlice';
import { helper } from '../utils/helper';
import Pagination from '../components/Pagination';
import EmptyState from '../components/EmptyState';
import PartnerCoupons from '../components/partner/PartnerCoupons';
import CopyableValue from '../components/partner/CopyableValue';
import {
  PeriodFilterDropdown,
  PeriodFilterValue,
} from '../components/dashboard/PeriodFilterDropdown';

const REFERRALS_PAGE_LIMIT = 20;

/**
 * Converts the shared period-filter value into the stats `from` / `to`
 * query params (Y-m-d). Both params are optional - no selection means the
 * all-time stats.
 */
const periodToStatsRange = (pv: PeriodFilterValue): { from?: string; to?: string } => {
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  if (pv.preset) {
    const now = new Date();
    let start: Date;
    let end: Date = now;
    switch (pv.preset) {
      case 'today':
        start = now;
        break;
      case '7':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
        break;
      case '30':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
        break;
      case 'this_month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last_month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'this_year':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        return {};
    }
    return { from: fmt(start), to: fmt(end) };
  }
  if (pv.startDate && pv.endDate) {
    return { from: pv.startDate, to: pv.endDate };
  }
  return {};
};

const PartnerDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { partnerStatus, me, stats, statsLoading, referrals, referralsLoading } = useSelector(
    (state: RootState) => state.affiliate
  );

  const [periodValue, setPeriodValue] = useState<PeriodFilterValue>({
    preset: null,
    startDate: null,
    endDate: null,
  });
  const [referralsPage, setReferralsPage] = useState(1);

  useEffect(() => {
    dispatch(fetchAffiliateMe());
  }, [dispatch]);

  useEffect(() => {
    if (partnerStatus !== 'partner') {
      return;
    }
    const { from, to } = periodToStatsRange(periodValue);
    dispatch(fetchAffiliateStats({ from, to }));
  }, [dispatch, partnerStatus, periodValue]);

  useEffect(() => {
    if (partnerStatus !== 'partner') {
      return;
    }
    dispatch(fetchAffiliateReferrals({ page: referralsPage, limit: REFERRALS_PAGE_LIMIT }));
  }, [dispatch, partnerStatus, referralsPage]);

  // Not a partner - the cabinet simply is not shown.
  if (partnerStatus === 'not_partner') {
    return <Navigate to="/dashboard" replace />;
  }

  if (partnerStatus !== 'partner' || !me) {
    return null;
  }

  const currency = stats?.currency || 'EUR';
  const formatCents = (cents: number) => helper.formatPriceCents(cents, currency);

  const statusLabel = (status: AffiliateReferralStatus) => {
    switch (status) {
      case 'registered':
        return t('affiliate.referral_status_registered');
      case 'trial':
        return t('affiliate.referral_status_trial');
      case 'paying':
        return t('affiliate.referral_status_paying');
      case 'churned':
        return t('affiliate.referral_status_churned');
      case 'deleted':
        return t('affiliate.referral_status_deleted');
      default:
        return status;
    }
  };

  const total = referrals?.total ?? 0;
  const limit = referrals?.limit ?? REFERRALS_PAGE_LIMIT;
  const page = referrals?.page ?? referralsPage;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    // `dashboard-stats` scopes the shared stat-card / stat-metric /
    // period-filter styles; `partner-dashboard` adds cabinet-specific bits.
    <div className="dashboard-stats partner-dashboard">
      <h1>{t('affiliate.cabinet_title')}</h1>

      {/* Partner profile: referral link, commission percent */}
      <div className="stat-card partner-info-card">
        <div className="partner-info-row">
          <span className="stat-metric-label">{t('affiliate.cabinet_ref_link')}</span>
          {/* Backend-built link - shown and copied as is. */}
          <CopyableValue value={me.referralLink} copyTitle={t('affiliate.cabinet_copy')} />
        </div>
        <div className="partner-info-row">
          <span className="stat-metric-label">{t('affiliate.cabinet_commission_percent')}</span>
          <span className="copyable-value-text">{me.commissionPercent}%</span>
        </div>
      </div>

      {/* Stats with an optional from/to period filter */}
      <div className="period-filter">
        <label>{t('affiliate.cabinet_period')}</label>
        <PeriodFilterDropdown value={periodValue} onChange={setPeriodValue} />
      </div>

      {stats && !statsLoading && (
        <>
          <div className="stat-card">
            <div className="stat-metrics-row">
              <div className="stat-metric">
                <span className="stat-metric-label">{t('affiliate.stats_registrations')}</span>
                <span className="stat-metric-value">{stats.registrations}</span>
              </div>
              <div className="stat-metric">
                <span className="stat-metric-label">{t('affiliate.stats_trials')}</span>
                <span className="stat-metric-value">{stats.trials}</span>
              </div>
              <div className="stat-metric">
                <span className="stat-metric-label">{t('affiliate.stats_paying')}</span>
                <span className="stat-metric-value">{stats.paying}</span>
              </div>
              <div className="stat-metric">
                <span className="stat-metric-label">{t('affiliate.stats_revenue_netto')}</span>
                <span className="stat-metric-value">{formatCents(stats.revenueNetto)}</span>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-header">
              <h3 className="stat-card-title">
                {t('affiliate.stats_commission_title', { percent: stats.commission.percent })}
              </h3>
            </div>
            <div className="stat-metrics-row">
              <div className="stat-metric">
                <span className="stat-metric-label">{t('affiliate.stats_commission_total')}</span>
                <span className="stat-metric-value">{formatCents(stats.commission.totalAccrued)}</span>
              </div>
              <div className="stat-metric">
                <span className="stat-metric-label">{t('affiliate.stats_commission_available')}</span>
                <span className="stat-metric-value">{formatCents(stats.commission.available)}</span>
              </div>
              <div className="stat-metric">
                <span className="stat-metric-label">{t('affiliate.stats_commission_on_hold')}</span>
                <span className="stat-metric-value">{formatCents(stats.commission.onHold)}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Referral list (emails are masked by the backend - shown as is) */}
      <h2 className="partner-referrals-title">{t('affiliate.referrals_title')}</h2>
      <div className="table-container">
        {referrals && referrals.items.length > 0 ? (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>{t('affiliate.referrals_email')}</th>
                  <th>{t('affiliate.referrals_registered_at')}</th>
                  <th>{t('affiliate.referrals_status')}</th>
                  <th>{t('affiliate.referrals_revenue')}</th>
                  <th>{t('affiliate.referrals_commission')}</th>
                </tr>
              </thead>
              <tbody>
                {referrals.items.map((item, index) => (
                  <tr key={`${item.email}-${index}`}>
                    <td>
                      {/* null email = the referral's account was deleted;
                          the earned commissions are kept. */}
                      {item.email ?? (
                        <span className="partner-referral-deleted">
                          {t('affiliate.referral_deleted_account')}
                        </span>
                      )}
                    </td>
                    <td>
                      {/* Deleted records have no registeredAt - show the
                          last payment date instead (per contract). */}
                      {item.registeredAt
                        ? helper.formatDate(item.registeredAt)
                        : item.lastPaidAt
                          ? helper.formatDate(item.lastPaidAt)
                          : '-'}
                    </td>
                    <td>{statusLabel(item.status)}</td>
                    <td>{formatCents(item.revenueNetto)}</td>
                    <td>{formatCents(item.commissionAccrued)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              startItem={startItem}
              endItem={endItem}
              total={total}
              totalPages={totalPages}
              page={page}
              loading={referralsLoading}
              handlePageChange={(direction) =>
                setReferralsPage((p) => (direction === 'next' ? p + 1 : Math.max(1, p - 1)))
              }
            />
          </>
        ) : (
          !referralsLoading && (
            <EmptyState
              title={t('affiliate.referrals_empty_title')}
              description={t('affiliate.referrals_empty_description')}
            />
          )
        )}
      </div>

      {/* Partner coupons: list / create / deactivate / redemptions */}
      <PartnerCoupons />
    </div>
  );
};

export default PartnerDashboardPage;
